
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UserProfile, SavedOutfit, AppPage, WardrobeItem } from '../types';
import { useTranslation } from '../i18n/useTranslation';
import Spinner from '../components/Spinner';
import { UsersIcon, SparklesIcon, AppleAppStoreIcon, GooglePlayStoreIcon, PlusIcon, Trash2Icon, InstagramIcon, TwitterIcon, YouTubeIcon, ShieldCheckIcon } from '../components/icons';
import { generateStylePersona } from '../services/geminiService';
import { membershipTiers, ON_DEMAND_FEATURES } from '../constants';
import { generateItemsFromRetailers } from '../services/retailService';
import { getAllUserProfiles, hasFeatureAccess } from '@shared/services/userService';
import Footer from '../components/Footer';
import ColorAnalysisModal from '../components/ColorAnalysisModal';
import PaymentModal from '../components/PaymentModal';
import AdminManagementModal from '../components/AdminManagementModal';
import PriceDisplay from '../components/PriceDisplay';
import { DiscountService } from '../services/discountService';
import { useLocalizedPrice } from '../utils/priceUtils';
import { useSEO } from '../hooks/useSEO';
import { useAppContext } from '../AppContext';
import { connectToService, disconnectFromService } from '@shared/services/socialMediaService';
import { trackEvent } from '../services/analyticsService';
import { GEMINI_MODELS } from '../config';

interface ProfilePageProps {
    currentUserId: string;
    savedOutfits: SavedOutfit[];
    onSaveToCloud: () => Promise<void>;
    onLoadFromCloud: () => Promise<void>;
    userProfile: UserProfile;
    setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
    onNavigate: (page: AppPage, params?: Record<string, string>) => void;
    onRestartOnboarding: () => void;
    allUsers: string[];
    onCreateNewUser: (name: string) => void;
    onDeleteUser: (userId: string) => void;
    onChangeUser: (newUserId: string) => Promise<void>;
    onOpenAuthModal: () => void;
    wardrobe: WardrobeItem[];
    setWardrobe: React.Dispatch<React.SetStateAction<WardrobeItem[]>>;
}

const RETAILERS = [
    'Zalando', 'ASOS', 'H&M', 'Zara', 'C&A', 'Mytheresa', 'Farfetch', 'Mango',
    'Net-a-Porter', 'MatchesFashion', 'Selfridges', 'Harrods', 'La Redoute',
    'El Corte Inglés', 'About You', 'Vinted', 'Vestiaire Collective', 'COS', '& Other Stories'
];

const ProfilePage: React.FC<ProfilePageProps> = ({
    currentUserId,
    savedOutfits,
    onSaveToCloud,
    onLoadFromCloud,
    userProfile,
    setUserProfile,
    onNavigate,
    onRestartOnboarding,
    allUsers,
    onCreateNewUser,
    onDeleteUser,
    onChangeUser,
    onOpenAuthModal,
    wardrobe,
    setWardrobe,
}) => {
    const { t, lang } = useTranslation();
    const { formatPrice } = useLocalizedPrice();
    const { toggleSocialChannel } = useAppContext();

    useSEO(
        t('profile_seo_title'),
        t('profile_seo_desc'),
        t('profile_seo_keywords').split(',').map(kw => kw.trim())
    );

    const [isPersonaLoading, setIsPersonaLoading] = useState(false);
    const [personaError, setPersonaError] = useState<string | null>(null);
    const [newUserName, setNewUserName] = useState('');
    const isGuest = currentUserId.includes('guest');
    const isAdmin = currentUserId.includes('admin') && !isGuest;
    const [selectedModels, setSelectedModels] = useState({
        image: GEMINI_MODELS.GEMINI_3_1_FLASH_IMAGE,
        flash: GEMINI_MODELS.GEMINI_3_1_FLASH,
        pro: GEMINI_MODELS.GEMINI_3_1_PRO,
        thinking: GEMINI_MODELS.GEMINI_3_1_PRO,
        video: GEMINI_MODELS.GEMINI_VEO_VIDEO
    });
    const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
    const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);

    // Payment State
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [pendingUpgrade, setPendingUpgrade] = useState<{ tierId: string, price: string, tierName: string, originalPriceEUR?: number } | null>(null);
    const [pendingOnDemand, setPendingOnDemand] = useState<{ id: string, name: string, price: number } | null>(null);

    const username = currentUserId.split('-')[0].charAt(0).toUpperCase() + currentUserId.split('-')[0].slice(1);

    const [loadedProfiles, setLoadedProfiles] = useState<any[]>([]);

    useEffect(() => {
        if (!isAdmin) return;
        const fetchProfiles = async () => {
            try {
                const profiles = await getAllUserProfiles();
                setLoadedProfiles(profiles);
            } catch (err) {
                console.error('Failed to load user profiles', err);
            }
        };
        fetchProfiles();
    }, [isAdmin]);

    const allUserProfiles = useMemo(() => {
        if (!isAdmin) return [];
        // Merge current user's state profile since it might have unsaved changes
        return loadedProfiles.map(p =>
            p.userId === currentUserId ? { userId: p.userId, profile: userProfile } : p
        );
    }, [isAdmin, currentUserId, userProfile, loadedProfiles]);

    // Load preferred models from local storage
    useEffect(() => {
        setSelectedModels({
            image: localStorage.getItem('model_image') || GEMINI_MODELS.GEMINI_3_1_FLASH_IMAGE,
            flash: localStorage.getItem('model_flash') || GEMINI_MODELS.GEMINI_3_1_FLASH,
            pro: localStorage.getItem('model_pro') || GEMINI_MODELS.GEMINI_3_1_PRO,
            thinking: localStorage.getItem('model_thinking') || GEMINI_MODELS.GEMINI_3_1_PRO,
            video: localStorage.getItem('model_video') || GEMINI_MODELS.GEMINI_VEO_VIDEO
        });
    }, []);

    const handleAnalyzeStyle = useCallback(async () => {
        if (savedOutfits.length === 0) {
            setPersonaError(t('profilePage_stylePersona_noOutfits'));
            return;
        }
        setIsPersonaLoading(true);
        setPersonaError(null);
        try {
            const persona = await generateStylePersona(savedOutfits, userProfile, lang);
            setUserProfile(prev => ({ ...prev, stylePersona: persona }));
        } catch (e) {
            setPersonaError(e instanceof Error ? e.message : t('profilePage_stylePersona_error'));
        } finally {
            setIsPersonaLoading(false);
        }
    }, [savedOutfits, userProfile, setUserProfile, t, lang]);

    const handleMeasurementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setUserProfile(prev => ({
            ...prev,
            measurements: {
                ...prev.measurements,
                [name]: value
            }
        }));
    };

    const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setUserProfile(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRetailerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = e.target;
        setUserProfile(prev => {
            const currentRetailers = new Set(prev.preferredRetailers || []);
            if (checked) {
                currentRetailers.add(value);
            } else {
                currentRetailers.delete(value);
            }
            return { ...prev, preferredRetailers: Array.from(currentRetailers) };
        });
    };

    const handleCreateUser = async () => {
        if (newUserName.trim()) {
            const name = newUserName.trim();
            onCreateNewUser(name);

            const sanitizedName = name.toLowerCase().replace(/\s+/g, '');
            const newUserId = `${sanitizedName}-user`;
            await onChangeUser(newUserId);

            setNewUserName('');
        }
    };

    const handleModelChange = (category: keyof typeof selectedModels, model: string) => {
        setSelectedModels(prev => ({ ...prev, [category]: model }));
        localStorage.setItem(`model_${category}`, model);
    };

    const initiateUpgrade = (tierId: string, price: string, tierName: string, originalPriceEUR?: number) => {
        setPendingUpgrade({ tierId, price, tierName, originalPriceEUR });
        setIsPaymentModalOpen(true);
    };

    const handleBookOnDemand = (feature: any) => {
        setPendingOnDemand({ id: feature.id, name: t(feature.nameKey), price: feature.priceEUR });
        setIsPaymentModalOpen(true);
    };

    const handlePaymentSuccess = () => {
        if (pendingUpgrade) {
            const now = new Date();
            const expiryDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

            trackEvent('membership_purchase', {
                tier_id: pendingUpgrade.tierId,
                tier_name: pendingUpgrade.tierName,
                price: pendingUpgrade.price
            });

            setUserProfile(prev => ({
                ...prev,
                membershipLevel: pendingUpgrade.tierId as 'basic' | 'premium' | 'advanced' | 'nextgen',
                membershipExpiry: expiryDate
            }));
            setPendingUpgrade(null);
        } else if (pendingOnDemand) {
            trackEvent('on_demand_purchase', {
                feature_id: pendingOnDemand.id,
                feature_name: pendingOnDemand.name,
                price: pendingOnDemand.price
            });

            const feature = ON_DEMAND_FEATURES.find(f => f.id === pendingOnDemand.id);
            const now = new Date();

            setUserProfile(prev => {
                const purchasedFeatures = { ...(prev.purchasedFeatures || {}) };
                const existing = purchasedFeatures[pendingOnDemand.id] || {};

                if (feature?.durationHours) {
                    const currentExpiry = existing.expiry ? new Date(existing.expiry) : new Date();
                    const newExpiry = new Date(Math.max(currentExpiry.getTime(), now.getTime()) + feature.durationHours * 60 * 60 * 1000);
                    purchasedFeatures[pendingOnDemand.id] = {
                        ...existing,
                        expiry: newExpiry.toISOString()
                    };
                } else if (feature?.amount) {
                    purchasedFeatures[pendingOnDemand.id] = {
                        ...existing,
                        remainingAmount: (existing.remainingAmount || 0) + feature.amount
                    };
                }

                return { ...prev, purchasedFeatures };
            });
            setPendingOnDemand(null);
        }
    };

    const handleUpdateProfileFromAnalysis = (attributes: Partial<UserProfile>) => {
        setUserProfile(prev => ({
            ...prev,
            ...attributes
        }));
    };

    const getMembershipStatus = useMemo(() => {
        const level = userProfile.membershipLevel || 'basic';
        const expiry = userProfile.membershipExpiry ? new Date(userProfile.membershipExpiry + 'T23:59:59') : null;
        const isExpired = expiry ? expiry < new Date() : false;

        if (level === 'basic') {
            return t('profilePage_membership_status_basic');
        }

        if (isExpired && expiry) {
            return t('profilePage_membership_status_expired', { level: t(`profilePage_membership_${level}` as any), date: expiry.toLocaleDateString() });
        }

        if (expiry) {
            return t('profilePage_membership_status_active', { level: t(`profilePage_membership_${level}` as any), date: expiry.toLocaleDateString() });
        }

        return t('profilePage_membership_status_active_no_date', { level: t(`profilePage_membership_${level}` as any) });
    }, [userProfile.membershipLevel, userProfile.membershipExpiry, t]);

    const tierNameMapping: Record<string, string> = useMemo(() => ({
        basic: t('profilePage_membership_basic'),
        premium: t('profilePage_membership_premium'),
        advanced: t('profilePage_membership_advanced'),
        nextgen: t('profilePage_membership_nextgen'),
    }), [t]);


    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="h-full w-full flex flex-col items-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900 overflow-y-auto custom-scrollbar"
        >
            <div className="w-full max-w-4xl space-y-8 mb-8">
                <div className="flex flex-col items-center relative py-6">
                    <div className="relative group">
                        <div className="w-24 h-24 sm:w-32 sm:h-32 bg-white dark:bg-gray-800 rounded-full shadow-2xl flex items-center justify-center mb-6 border-4 border-white dark:border-gray-800 overflow-hidden ring-2 ring-indigo-500/30 animate-glow">
                            <UsersIcon className="w-12 h-12 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div className="absolute -bottom-2 -right-2 bg-indigo-600 text-white p-2 rounded-full shadow-lg border-2 border-white dark:border-gray-950">
                            <SparklesIcon className="w-4 h-4" />
                        </div>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-serif font-bold text-gray-900 dark:text-white text-center tracking-tight leading-tight">
                        {t('profilePage_title')}
                    </h1>
                    <div className="mt-4 flex items-center gap-3">
                        <span className="text-gray-600 dark:text-gray-400 font-medium bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm px-4 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm">
                            @{username}
                        </span>
                        {userProfile.membershipLevel && userProfile.membershipLevel !== 'basic' && (
                            <span className="px-3 py-1 bg-indigo-600 text-white rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg">
                                {tierNameMapping[userProfile.membershipLevel] || userProfile.membershipLevel}
                            </span>
                        )}
                    </div>
                </div>

                {/* Admin User Management - Only shown for Admin */}
                {isAdmin && (
                    <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-6 md:p-8 border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="flex items-center justify-between mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">
                            <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-white flex items-center gap-3">
                                <span className="bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400 p-2 rounded-lg">
                                    <UsersIcon className="w-6 h-6" />
                                </span>
                                {t('profilePage_admin_title')}
                            </h2>
                            <button
                                onClick={() => setIsAdminModalOpen(true)}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-xl transition-all shadow-lg flex items-center gap-2 text-sm active:scale-95"
                            >
                                <ShieldCheckIcon className="w-5 h-5" />
                                Admin Control Center
                            </button>
                        </div>

                        {/* User List Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                            {allUsers.map(user => {
                                const isCurrentUser = user === currentUserId;
                                const displayName = user.split('-')[0].charAt(0).toUpperCase() + user.split('-')[0].slice(1);
                                return (
                                    <div key={user} className={`relative p-5 rounded-xl border transition-all duration-200 flex flex-col ${isCurrentUser ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-800 ring-1 ring-indigo-500 shadow-md' : 'bg-white dark:bg-gray-700/30 border-gray-200 dark:border-gray-600 hover:shadow-lg hover:-translate-y-0.5'}`}>
                                        <div className="flex items-start justify-between mb-4">
                                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-sm ${isCurrentUser ? 'bg-indigo-600 text-white' : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'}`}>
                                                {user.charAt(0).toUpperCase()}
                                            </div>
                                            {isCurrentUser && (
                                                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                                    {t('nav_user_status_current')}
                                                </span>
                                            )}
                                        </div>

                                        <h3 className="font-bold text-lg text-gray-900 dark:text-white truncate mb-1">{displayName}</h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 font-mono bg-gray-100 dark:bg-gray-800 inline-block px-2 py-1 rounded w-fit">
                                            {user}
                                        </p>

                                        <div className="flex gap-2 mt-auto pt-3 border-t border-gray-100 dark:border-gray-600/50">
                                            {!isCurrentUser ? (
                                                <>
                                                    <button
                                                        onClick={() => onChangeUser(user)}
                                                        className="flex-1 py-2 px-3 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200 text-white text-xs font-bold uppercase tracking-wide rounded-lg transition-colors shadow-sm"
                                                    >
                                                        {t('nav_user_action_login')}
                                                    </button>
                                                    <button
                                                        onClick={() => onDeleteUser(user)}
                                                        className="p-2 text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 bg-transparent hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                        title={t('nav_user_action_delete')}
                                                    >
                                                        <Trash2Icon className="w-4 h-4" />
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="w-full py-2 px-3 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-bold uppercase tracking-wide rounded-lg text-center">
                                                    {t('nav_user_status_active')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Create User Form */}
                        <div className="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
                            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">{t('profilePage_admin_createUser')}</h3>
                            <div className="flex gap-3">
                                <div className="relative flex-grow">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <UsersIcon className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={newUserName}
                                        onChange={(e) => setNewUserName(e.target.value)}
                                        placeholder={t('profilePage_admin_username_placeholder')}
                                        className="w-full pl-10 p-3 text-sm border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm transition-shadow"
                                    />
                                </div>
                                <button onClick={handleCreateUser} className="flex items-center gap-2 bg-indigo-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-indigo-700 text-sm transition-colors shadow-md hover:shadow-lg active:scale-95">
                                    <PlusIcon className="w-5 h-5" /> {t('profilePage_admin_createButton')}
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 pl-1 flex items-center gap-1">
                                <span className="inline-block w-1 h-1 rounded-full bg-gray-400"></span>
                                {t('profilePage_admin_suffix_note')}
                            </p>
                        </div>

                        {/* Model Configuration */}
                        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-5 uppercase tracking-wide">{t('nav_admin_model_config')}</h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Image Generation Model */}
                                <div className="flex flex-col gap-3 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
                                    <div className="flex-grow">
                                        <label htmlFor="admin-model-image" className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">{t('nav_admin_model_image_label')}</label>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('nav_admin_model_image_desc')}</p>
                                    </div>
                                    <select
                                        id="admin-model-image"
                                        value={selectedModels.image}
                                        onChange={(e) => handleModelChange('image', e.target.value)}
                                        className="w-full p-2.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 transition-all"
                                    >
                                        <option value={GEMINI_MODELS.GEMINI_3_PRO_IMAGE}>Gemini 3 Pro Image</option>
                                        <option value={GEMINI_MODELS.GEMINI_2_5_FLASH_IMAGE}>Gemini 2.5 Flash Image</option>
                                        <option value={GEMINI_MODELS.GEMINI_2_0_FLASH}>Gemini 2.0 Flash (Next-Gen)</option>
                                        <option value={GEMINI_MODELS.GEMINI_3_1_FLASH_IMAGE}>Gemini 3.1 Flash Image</option>
                                    </select>
                                </div>

                                {/* Flash Analysis Model */}
                                <div className="flex flex-col gap-3 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
                                    <div className="flex-grow">
                                        <label htmlFor="admin-model-flash" className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">{t('nav_admin_model_flash_label')}</label>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('nav_admin_model_flash_desc')}</p>
                                    </div>
                                    <select
                                        id="admin-model-flash"
                                        value={selectedModels.flash}
                                        onChange={(e) => handleModelChange('flash', e.target.value)}
                                        className="w-full p-2.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 transition-all"
                                    >

                                        <option value={GEMINI_MODELS.GEMINI_2_5_FLASH_LITE}>Gemini 2.5 Flash Lite</option>
                                        <option value={GEMINI_MODELS.GEMINI_3_1_FLASH}>Gemini 3.1 Flash</option>
                                        <option value={GEMINI_MODELS.FLASH_TEXT}>{GEMINI_MODELS.FLASH_TEXT} (Default 2.0)</option>
                                        <option value={GEMINI_MODELS.GEMINI_1_5_FLASH}>Gemini 1.5 Flash (Stable)</option>
                                    </select>
                                </div>

                                {/* Pro Reasoning Model */}
                                <div className="flex flex-col gap-3 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
                                    <div className="flex-grow">
                                        <label htmlFor="admin-model-pro" className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">{t('nav_admin_model_pro_label')}</label>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('nav_admin_model_pro_desc')}</p>
                                    </div>
                                    <select
                                        id="admin-model-pro"
                                        value={selectedModels.pro}
                                        onChange={(e) => handleModelChange('pro', e.target.value)}
                                        className="w-full p-2.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 transition-all"
                                    >
                                        <option value={GEMINI_MODELS.GEMINI_3_1_PRO}>Gemini 3.1 Pro (Preview/Fallback)</option>
                                        <option value={GEMINI_MODELS.GEMINI_3_PRO}>Gemini 3.0 Pro (Preview/Fallback)</option>
                                        <option value={GEMINI_MODELS.GEMINI_2_5_PRO}>Gemini 2.5 Pro</option>
                                        <option value={GEMINI_MODELS.PRO_TEXT}>{GEMINI_MODELS.PRO_TEXT} (Latest 2.0)</option>
                                        <option value={GEMINI_MODELS.GEMINI_1_5_PRO}>Gemini 1.5 Pro (Stable)</option>
                                    </select>
                                </div>

                                {/* Deep Think Model */}
                                <div className="flex flex-col gap-3 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
                                    <div className="flex-grow">
                                        <label htmlFor="admin-model-thinking" className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">{t('nav_admin_model_thinking_label')}</label>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('nav_admin_model_thinking_desc')}</p>
                                    </div>
                                    <select
                                        id="admin-model-thinking"
                                        value={selectedModels.thinking}
                                        onChange={(e) => handleModelChange('thinking', e.target.value)}
                                        className="w-full p-2.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 transition-all"
                                    >
                                        <option value={GEMINI_MODELS.DEEP_THINK}>{GEMINI_MODELS.DEEP_THINK} (Reasoning)</option>
                                    </select>
                                </div>

                                {/* Video Generation Model */}
                                <div className="flex flex-col gap-3 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-200 dark:border-gray-600 md:col-span-2">
                                    <div className="flex-grow">
                                        <label htmlFor="admin-model-video" className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">{t('nav_admin_model_video_label')}</label>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('nav_admin_model_video_desc')}</p>
                                    </div>
                                    <select
                                        id="admin-model-video"
                                        value={selectedModels.video}
                                        onChange={(e) => handleModelChange('video', e.target.value)}
                                        className="w-full p-2.5 text-sm border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 transition-all"
                                    >
                                        <option value={GEMINI_MODELS.VEO_3_1_FAST}>Veo 3.1 Fast</option>
                                        <option value={GEMINI_MODELS.GEMINI_VEO_VIDEO}>Veo 1.0 (Standard)</option>
                                        <option value="veo-2.0-generate-001">Veo 2.0 (Stable)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Regular Profile Content - Hidden for Admin */}
                {!isAdmin && (
                    <>
                        <div className="glass dark:bg-gray-800/50 shadow-2xl rounded-3xl p-6 md:p-10 border border-white/20 dark:border-gray-700/50">
                            <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-3">
                                <SparklesIcon className="w-8 h-8 text-indigo-500 drop-shadow-[0_0_8px_rgba(99,102,241,0.5)]" />
                                {t('profilePage_stylePersona_title')}
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">{t('profilePage_stylePersona_description')}</p>
                            <div className="p-8 bg-white/40 dark:bg-gray-900/40 rounded-2xl min-h-[120px] flex items-center justify-center border border-white/30 dark:border-white/5 shadow-inner group transition-all hover:bg-white/50 dark:hover:bg-gray-900/50 mt-4 mb-2">
                                {isPersonaLoading ? (
                                    <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
                                        <Spinner className="w-5 h-5" />
                                        <span>{t('profilePage_stylePersona_analyzing_dna')}</span>
                                    </div>
                                ) : personaError ? (
                                    <p className="text-red-500 font-medium">{personaError}</p>
                                ) : userProfile.stylePersona ? (
                                    <p className="italic text-gray-800 dark:text-gray-200 text-center font-serif text-lg leading-relaxed">"{userProfile.stylePersona}"</p>
                                ) : (
                                    <p className="text-gray-500 text-center">{t('profilePage_stylePersona_noOutfits')}</p>
                                )}
                            </div>
                            {!isGuest ? (
                                <button onClick={handleAnalyzeStyle} disabled={isPersonaLoading || savedOutfits.length === 0} className="mt-4 bg-indigo-600 text-white font-semibold py-2 px-5 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm">
                                    {isPersonaLoading ? t('profilePage_stylePersona_analyzing') : t('profilePage_stylePersona_analyzeButton')}
                                </button>
                            ) : (
                                <button onClick={onOpenAuthModal} className="mt-4 bg-gray-900 dark:bg-white dark:text-gray-900 text-white font-bold py-2.5 px-6 rounded-lg hover:opacity-90 transition-opacity shadow-sm flex items-center gap-2">
                                    <SparklesIcon className="w-4 h-4" />
                                    {t('profilePage_register_to_unlock', 'Register to unlock')}
                                </button>
                            )}
                        </div>

                        <div className="glass dark:bg-gray-800/40 shadow-2xl rounded-3xl p-6 md:p-10 border border-white/20 dark:border-gray-700/50">
                            <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start mb-8 gap-4">
                                <div>
                                    <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-2">{t('profilePage_measurements_title')}</h2>
                                    <p className="text-gray-600 dark:text-gray-400 max-w-md">{t('profilePage_measurements_description')}</p>
                                </div>
                                <button
                                    onClick={() => {
                                        if (hasFeatureAccess(userProfile, 'color_analysis', 'premium')) {
                                            setIsAnalysisModalOpen(true);
                                        } else {
                                            const feature = ON_DEMAND_FEATURES.find(f => f.id === 'color_analysis');
                                            if (feature) handleBookOnDemand(feature);
                                        }
                                    }}
                                    className="bg-indigo-600/10 hover:bg-indigo-600/20 text-indigo-600 dark:text-indigo-400 px-6 py-2.5 rounded-2xl font-bold flex items-center gap-2 transition-all border border-indigo-200/50 dark:border-indigo-500/20 active:scale-95"
                                >
                                    {hasFeatureAccess(userProfile, 'color_analysis', 'premium') ? (
                                        <SparklesIcon className="w-5 h-5" />
                                    ) : (
                                        <ShieldCheckIcon className="w-5 h-5" />
                                    )}
                                    {t('profilePage_measurements_ai_auto_detect')}
                                </button>
                            </div>

                            {/* Card-Based Measurements Grid */}
                            <div className="space-y-10">
                                <section>
                                    <h3 className="text-sm font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-6 flex items-center gap-4">
                                        {t('profilePage_personalDetails_title')}
                                        <div className="h-px flex-grow bg-gradient-to-r from-gray-200 dark:from-gray-700 to-transparent"></div>
                                    </h3>
                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                                        {[
                                            { id: 'height', label: t('profilePage_measurements_height') },
                                            { id: 'weight', label: t('profilePage_measurements_weight') },
                                            { id: 'chest', label: t('profilePage_measurements_chest') },
                                            { id: 'waist', label: t('profilePage_measurements_waist') },
                                            { id: 'hips', label: t('profilePage_measurements_hips') },
                                            { id: 'shoeSize', label: t('profilePage_measurements_shoeSize') }
                                        ].map(item => (
                                            <div key={item.id} className="bg-white/40 dark:bg-gray-900/40 p-4 rounded-2xl border border-white/50 dark:border-white/5 flex flex-col gap-1 transition-all hover:shadow-lg group">
                                                <label htmlFor={item.id} className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest group-hover:text-indigo-500 transition-colors">{item.label}</label>
                                                <input
                                                    type="text"
                                                    name={item.id}
                                                    id={item.id}
                                                    value={(userProfile.measurements as any)?.[item.id] || ''}
                                                    onChange={handleMeasurementChange}
                                                    placeholder="--"
                                                    className="w-full bg-transparent p-0 text-xl font-serif font-bold text-gray-800 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                <section>
                                    <h3 className="text-sm font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-6 flex items-center gap-4">
                                        {t('profilePage_attributes_detected_title')}
                                        <div className="h-px flex-grow bg-gradient-to-r from-gray-200 dark:from-gray-700 to-transparent"></div>
                                    </h3>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                        {[
                                            { id: 'colorSeason', label: t('profilePage_measurements_colorSeason_label'), value: userProfile.colorSeason },
                                            { id: 'bodySize', label: t('profilePage_attributes_bodySize'), value: userProfile.bodySize },
                                            { id: 'hairColor', label: t('profilePage_attributes_hairColor'), value: userProfile.hairColor },
                                            { id: 'eyeColor', label: t('profilePage_attributes_eyeColor'), value: userProfile.eyeColor }
                                        ].map(item => (
                                            <div key={item.id} className="bg-white/40 dark:bg-gray-900/40 p-4 rounded-2xl border border-white/50 dark:border-white/5 flex flex-col gap-1 transition-all hover:shadow-lg group">
                                                <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest group-hover:text-indigo-500 transition-colors">{item.label}</label>
                                                <span className="text-lg font-serif font-bold text-gray-800 dark:text-gray-100">
                                                    {item.value || t('profilePage_attributes_notDetected')}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            </div>
                        </div>
                        {isGuest && (
                            <div className="mt-8 p-6 bg-indigo-50 dark:bg-indigo-900/10 rounded-2xl border border-indigo-100 dark:border-indigo-900/30 flex flex-col md:flex-row items-center justify-between gap-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white shadow-lg shrink-0">
                                        <SparklesIcon className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-900 dark:text-white">{t('profilePage_guest_cta_title', 'Unlock Your Digital Style Twin')}</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">{t('profilePage_guest_cta_desc', 'Register to save your measurements, analyze your style, and sync across devices.')}</p>
                                    </div>
                                </div>
                                <button onClick={onOpenAuthModal} className="whitespace-nowrap bg-indigo-600 text-white font-bold py-3 px-8 rounded-xl hover:bg-indigo-700 transition-all shadow-md active:scale-95">
                                    {t('nav_user_action_register', 'Register Now')}
                                </button>
                            </div>
                        )}

                        <div className={`glass dark:bg-gray-800/50 shadow-2xl rounded-3xl p-6 md:p-10 border border-white/20 dark:border-gray-700/50 ${isGuest ? 'opacity-50 grayscale pointer-events-none select-none overflow-hidden max-h-48 relative' : ''}`}>
                            <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-2">{t('profilePage_preferredRetailers_label')}</h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-2xl">{t('profilePage_preferredRetailers_description')}</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                {RETAILERS.map(retailer => (
                                    <label key={retailer} className="flex items-center space-x-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <input
                                            type="checkbox"
                                            value={retailer}
                                            checked={userProfile.preferredRetailers?.includes(retailer) || false}
                                            onChange={handleRetailerChange}
                                            className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 transition-colors"
                                        />
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{retailer}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
                                <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                                    <SparklesIcon className="w-5 h-5 text-amber-500" />
                                    <span>{t('profilePage_retailers_sync_hint', 'Populate your wardrobe with sample items from selected retailers.')}</span>
                                </div>
                                <button
                                    onClick={() => {
                                        const newItems = generateItemsFromRetailers(userProfile.preferredRetailers || []);
                                        newItems.forEach(item => {
                                            // Ensure we don't add duplicates
                                            if (!wardrobe.some(w => w.nameKey === item.nameKey && w.brand === item.brand)) {
                                                setWardrobe(prev => [item, ...prev]);
                                            }
                                        });
                                        // Optional: Show success toast or navigate
                                        onNavigate('canvas');
                                    }}
                                    disabled={!userProfile.preferredRetailers?.length}
                                    className="whitespace-nowrap bg-gray-900 dark:bg-white dark:text-gray-900 text-white font-bold py-3 px-8 rounded-xl hover:opacity-90 transition-all shadow-md active:scale-95 disabled:opacity-50 disabled:grayscale disabled:pointer-events-none"
                                >
                                    {t('profilePage_retailers_sync_button', 'Sync Retailer Items')}
                                </button>
                            </div>
                            {isGuest && <div className="absolute inset-0 bg-transparent" />}
                        </div>

                        <div id="membership" className={`glass dark:bg-gray-800/40 shadow-2xl rounded-3xl p-6 md:p-10 border border-white/20 dark:border-gray-700/50 ${isAdmin ? '' : (isGuest ? 'opacity-50 grayscale pointer-events-none select-none overflow-hidden max-h-48 relative' : '')}`}>
                            <div className="flex justify-between items-center mb-8">
                                <h2 className="text-3xl font-serif font-semibold text-gray-900 dark:text-white">{t('profilePage_membership_label')}</h2>
                                {isAdmin && (
                                    <div className="flex gap-3">
                                        <select
                                            name="membershipLevel"
                                            title="Membership Level"
                                            value={userProfile.membershipLevel}
                                            onChange={handleProfileChange}
                                            className="bg-white/50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl p-2 text-sm backdrop-blur-sm"
                                        >
                                            {membershipTiers.map(tier => (
                                                <option key={tier.id} value={tier.id}>{tier.id}</option>
                                            ))}
                                        </select>
                                        <input
                                            type="date"
                                            name="membershipExpiry"
                                            title="Membership Expiry"
                                            value={userProfile.membershipExpiry ? new Date(userProfile.membershipExpiry).toISOString().split('T')[0] : ''}
                                            onChange={handleProfileChange}
                                            className="bg-white/50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl p-2 text-sm backdrop-blur-sm"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="bg-indigo-500/10 dark:bg-indigo-400/10 p-5 rounded-2xl mb-10 border border-indigo-200/50 dark:border-indigo-800/50 backdrop-blur-md">
                                <p className="text-indigo-900 dark:text-indigo-200 font-semibold flex items-center gap-3">
                                    <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 animate-pulse"></span>
                                    {getMembershipStatus}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {membershipTiers.map(tier => {
                                    const priceInfo = tier.prices.find(p => p.cycle === 'monthly');
                                    const isCurrentPlan = (userProfile.membershipLevel || 'basic') === tier.id;

                                    const tierStyles: Record<string, string> = {
                                        basic: 'from-blue-500/5 to-blue-600/5 border-blue-200/30 dark:border-blue-800/30 hover:border-blue-400/50',
                                        premium: 'from-amber-400/10 to-amber-600/10 border-amber-200/30 dark:border-amber-800/30 hover:border-amber-400/50 glow-border',
                                        advanced: 'from-purple-500/10 to-purple-600/10 border-purple-200/30 dark:border-purple-800/30 hover:border-purple-400/50 glow-border',
                                        nextgen: 'from-indigo-500/10 to-cyan-400/10 border-cyan-200/30 dark:border-cyan-800/30 hover:border-cyan-400/50 glow-border'
                                    };

                                    return (
                                        <div
                                            key={tier.id}
                                            className={`relative overflow-hidden group rounded-3xl border p-8 flex flex-col transition-all duration-500 bg-gradient-to-br ${tierStyles[tier.id] || 'from-gray-500/5 to-gray-600/5 border-gray-200 dark:border-gray-700'} ${tier.isFeatured ? 'ring-2 ring-indigo-500/50 shadow-2xl scale-[1.02] z-10' : 'shadow-lg hover:shadow-xl'}`}
                                        >
                                            {tier.isFeatured && (
                                                <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-[10px] font-black uppercase tracking-widest py-1.5 px-4 rounded-b-2xl shadow-lg z-20">
                                                    {t('nav_membership_recommended')}
                                                </div>
                                            )}

                                            {isCurrentPlan && (
                                                <div className="absolute top-4 right-4 text-indigo-600 dark:text-indigo-400">
                                                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                                                        <span className="text-lg font-bold">✓</span>
                                                    </div>
                                                </div>
                                            )}

                                            <h3 className="text-2xl font-bold font-serif text-gray-900 dark:text-white mb-2">{t(tier.nameKey)}</h3>

                                            <div className="mt-2 relative flex items-baseline gap-2">
                                                <PriceDisplay
                                                    basePriceEUR={priceInfo?.basePriceEUR || 0}
                                                    tierId={tier.id}
                                                    fallbackKey={priceInfo?.priceKey}
                                                    className="items-baseline"
                                                />
                                                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-tighter">/ {t('profilePage_membership_month', 'mo')}</span>
                                            </div>

                                            <ul className="mt-8 space-y-4 flex-grow">
                                                {tier.featuresKey.map(feature => (
                                                    <li key={feature} className="flex items-start group/item">
                                                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/10 dark:bg-green-400/10 text-green-600 dark:text-green-400 flex items-center justify-center mt-0.5 mr-3 group-hover/item:scale-110 transition-transform">
                                                            <span className="text-[10px] font-bold">✓</span>
                                                        </div>
                                                        <span className="text-sm text-gray-600 dark:text-gray-300 group-hover/item:text-gray-900 dark:group-hover/item:text-white transition-colors">{t(feature)}</span>
                                                    </li>
                                                ))}
                                            </ul>

                                            <button
                                                onClick={() => {
                                                    const discount = DiscountService.calculateDiscount(priceInfo?.basePriceEUR || 0, undefined, tier.id);
                                                    if (isGuest) {
                                                        onOpenAuthModal();
                                                    } else if (!isCurrentPlan) {
                                                        initiateUpgrade(tier.id, discount.discountedPrice.toString(), t(tier.nameKey), priceInfo?.basePriceEUR);
                                                    }
                                                }}
                                                disabled={isCurrentPlan}
                                                className={`w-full mt-8 font-bold py-4 px-6 rounded-2xl transition-all duration-300 active:scale-95 text-sm shadow-lg ${isCurrentPlan
                                                    ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border border-indigo-200/50 dark:border-indigo-800/50 cursor-default opacity-80'
                                                    : tier.isFeatured
                                                        ? 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-indigo-500/40'
                                                        : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 hover:border-indigo-500/50 hover:bg-gray-50 dark:hover:bg-gray-700/80 shadow-sm'
                                                    }`}
                                            >
                                                {isCurrentPlan ? t('profilePage_membership_current_label') : (isGuest ? t('nav_user_action_register') : t('profilePage_membership_upgrade_button'))}
                                            </button>

                                            <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-indigo-500/5 group-hover:bg-indigo-500/10 rounded-full blur-3xl transition-colors pointer-events-none" />
                                        </div>
                                    );
                                })}
                            </div>

                            {['premium', 'advanced', 'nextgen'].includes(userProfile.membershipLevel || '') && (
                                <div className="mt-16 pt-12 border-t border-white/10 dark:border-gray-700/50">
                                    <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
                                        <div className="flex-1 text-center lg:text-left">
                                            <h3 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-4">{t('profilePage_mobileApp_title')}</h3>
                                            <p className="text-gray-600 dark:text-gray-400 text-lg max-w-lg mb-10 leading-relaxed">{t('profilePage_mobileApp_description')}</p>
                                            <div className="flex flex-wrap justify-center lg:justify-start gap-6">
                                                <button className="flex items-center gap-4 bg-black text-white hover:bg-gray-900 py-3.5 px-6 rounded-2xl transition-all shadow-xl hover:-translate-y-1 active:scale-95 border border-white/10">
                                                    <AppleAppStoreIcon className="w-8 h-8" />
                                                    <div className="text-left">
                                                        <p className="text-[10px] font-medium uppercase tracking-widest opacity-60 leading-none mb-1">{t('nav_app_download_prefix_ios')}</p>
                                                        <p className="text-lg font-bold leading-tight">{t('nav_app_store_ios')}</p>
                                                    </div>
                                                </button>
                                                <button className="flex items-center gap-4 bg-black text-white hover:bg-gray-900 py-3.5 px-6 rounded-2xl transition-all shadow-xl hover:-translate-y-1 active:scale-95 border border-white/10">
                                                    <GooglePlayStoreIcon className="w-8 h-8" />
                                                    <div className="text-left">
                                                        <p className="text-[10px] font-medium uppercase tracking-widest opacity-60 leading-none mb-1">{t('nav_app_download_prefix_android')}</p>
                                                        <p className="text-lg font-bold leading-tight">{t('nav_app_store_android')}</p>
                                                    </div>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="glass dark:bg-gray-700/40 p-8 rounded-3xl shadow-2xl border border-white/20 dark:border-gray-600/30 flex flex-col items-center gap-6 transform hover:rotate-1 transition-transform">
                                            <p className="text-[11px] font-black text-indigo-500 dark:text-indigo-400 uppercase tracking-[0.2em]">{t('profilePage_mobileApp_qrTitle')}</p>
                                            <div className="flex gap-6">
                                                <div className="w-28 h-28 bg-white dark:bg-gray-900/50 rounded-2xl flex items-center justify-center border border-gray-100 dark:border-gray-800 group relative cursor-pointer overflow-hidden shadow-inner">
                                                    <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#000_2px,transparent_2px)] [background-size:6px_6px]" />
                                                    <AppleAppStoreIcon className="w-10 h-10 text-gray-300 group-hover:text-indigo-500 transition-colors group-hover:scale-110 duration-300" />
                                                    <div className="absolute bottom-2 right-2 w-3 h-3 bg-indigo-500 rounded-md" />
                                                    <div className="absolute top-2 left-2 w-3 h-3 bg-indigo-500 rounded-md" />
                                                </div>
                                                <div className="w-28 h-28 bg-white dark:bg-gray-900/50 rounded-2xl flex items-center justify-center border border-gray-100 dark:border-gray-800 group relative cursor-pointer overflow-hidden shadow-inner">
                                                    <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#000_2px,transparent_2px)] [background-size:6px_6px]" />
                                                    <GooglePlayStoreIcon className="w-10 h-10 text-gray-300 group-hover:text-indigo-500 transition-colors group-hover:scale-110 duration-300" />
                                                    <div className="absolute bottom-2 right-2 w-3 h-3 bg-indigo-500 rounded-md" />
                                                    <div className="absolute top-2 left-2 w-3 h-3 bg-indigo-500 rounded-md" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* On-Demand Services */}
                        <div className={`glass dark:bg-gray-800/50 shadow-2xl rounded-3xl p-6 md:p-10 border border-white/20 dark:border-gray-700/50 ${isGuest ? 'opacity-50 grayscale pointer-events-none select-none' : ''}`}>
                            <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-2">{t('profilePage_onDemand_title', 'On-Demand Services')}</h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-2xl">{t('profilePage_onDemand_description', 'Book high-value features for a single use or limited duration.')}</p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {ON_DEMAND_FEATURES.map(feature => {
                                    const hasAccess = hasFeatureAccess(userProfile, feature.id);
                                    const purchased = userProfile.purchasedFeatures?.[feature.id];

                                    return (
                                        <div key={feature.id} className="bg-white/40 dark:bg-gray-900/40 p-6 rounded-2xl border border-white/50 dark:border-white/5 flex flex-col h-full transition-all hover:shadow-xl group">
                                            <div className="flex justify-between items-start mb-4">
                                                <h3 className="font-bold text-lg text-gray-900 dark:text-white group-hover:text-indigo-500 transition-colors">{t(feature.nameKey, feature.nameKey)}</h3>
                                                {hasAccess && (
                                                    <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-bold uppercase py-1 px-2 rounded-full">
                                                        {t('common_active', 'Active')}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                                                {t(feature.descriptionKey, feature.descriptionKey)}
                                            </p>

                                            {purchased && (
                                                <div className="mb-4 p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-[10px] font-semibold text-indigo-700 dark:text-indigo-300">
                                                    {purchased.expiry && (
                                                        <div>{t('on_demand_status_expiry', 'Expires: ')} {new Date(purchased.expiry).toLocaleString()}</div>
                                                    )}
                                                    {purchased.remainingAmount !== undefined && (
                                                        <div>{t('on_demand_status_remaining', 'Remaining: ')} {purchased.remainingAmount}</div>
                                                    )}
                                                </div>
                                            )}

                                            <button
                                                onClick={() => handleBookOnDemand(feature)}
                                                className={`w-full py-2.5 rounded-xl font-bold text-xs transition-all shadow-sm active:scale-95 ${hasAccess
                                                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-indigo-600 hover:text-white'
                                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                                    }`}
                                            >
                                                {hasAccess ? t('on_demand_book_again', 'Extend / Re-book') : `${t('common_book', 'Book')} - ${formatPrice(feature.priceEUR)}`}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className={`glass dark:bg-gray-800/50 shadow-2xl rounded-3xl p-6 md:p-10 border border-white/20 dark:border-gray-700/50 ${isGuest ? 'opacity-50 grayscale pointer-events-none select-none' : ''}`}>
                            <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-4">{t('profilePage_settings_title')}</h2>
                            <div className="relative">
                                <label htmlFor="saveImageQuality" className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">{t('profilePage_settings_quality_label')}</label>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('profilePage_settings_quality_desc')}</p>
                                <select
                                    id="saveImageQuality"
                                    name="saveImageQuality"
                                    value={userProfile.saveImageQuality || 'medium'}
                                    onChange={handleProfileChange}
                                    className="w-full max-w-xs p-3 bg-white/50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-500 backdrop-blur-sm transition-all"
                                >
                                    <option value="low">{t('profilePage_settings_quality_low')}</option>
                                    <option value="medium">{t('profilePage_settings_quality_medium')}</option>
                                    <option value="high">{t('profilePage_settings_quality_high')}</option>
                                </select>
                                {isGuest && <div className="absolute inset-0 bg-transparent" />}
                            </div>
                        </div>

                        <div className={`glass dark:bg-gray-800/50 shadow-2xl rounded-3xl p-6 md:p-10 border border-white/20 dark:border-gray-700/50 ${isGuest ? 'hidden' : ''}`}>
                            <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-2">{t('profilePage_socialConnections_title', 'Social Media Connections')}</h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-2xl">{t('profilePage_socialConnections_description', 'Connect your social media accounts to sync your feeds and content.')}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                {[
                                    { id: 'instagram', name: 'Instagram', icon: <InstagramIcon />, color: 'text-pink-600' },
                                    { id: 'twitter', name: 'Twitter / X', icon: <TwitterIcon />, color: 'text-blue-400' },
                                    { id: 'youtube', name: 'YouTube', icon: <YouTubeIcon />, color: 'text-red-600' }
                                ].map(channel => {
                                    const isConnected = userProfile.socialChannels?.includes(channel.id);
                                    return (
                                        <div key={channel.id} className="p-6 rounded-2xl border border-white/30 dark:border-white/5 bg-white/40 dark:bg-gray-900/40 flex flex-col items-center group hover:shadow-xl transition-all">
                                            <div className={`w-12 h-12 mb-4 flex items-center justify-center ${channel.color} group-hover:scale-110 transition-transform`}>
                                                {channel.icon}
                                            </div>
                                            <span className="font-bold text-gray-900 dark:text-white mb-4">{channel.name}</span>
                                            <button
                                                onClick={async () => {
                                                    if (isConnected) {
                                                        const success = await disconnectFromService(channel.id as any);
                                                        if (success) toggleSocialChannel(channel.id, false);
                                                    } else {
                                                        const success = await connectToService(channel.id as any);
                                                        if (success) toggleSocialChannel(channel.id, true);
                                                    }
                                                }}
                                                className={`w-full py-3 px-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${isConnected
                                                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                                                    : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 active:scale-95'
                                                    }`}
                                            >
                                                {isConnected ? t('common_disconnect', 'Disconnect') : t('common_connect', 'Connect')}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className={`glass dark:bg-gray-800/50 shadow-2xl rounded-3xl p-6 md:p-10 border border-white/20 dark:border-gray-700/50 ${isGuest ? 'hidden' : ''}`}>
                            <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-2">{t('profilePage_cloudSync_title')}</h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-2xl">{t('profilePage_cloudSync_description')}</p>
                            <div className="flex gap-4">
                                <button onClick={onSaveToCloud} className="bg-indigo-600 text-white font-bold py-3.5 px-8 rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/20 active:scale-95">
                                    {t('profilePage_dataManagement_save')}
                                </button>
                                <button onClick={onLoadFromCloud} className="bg-white/50 dark:bg-gray-700/50 text-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 font-bold py-3.5 px-8 rounded-xl hover:bg-white dark:hover:bg-gray-600 transition-all backdrop-blur-sm">
                                    {t('profilePage_dataManagement_load')}
                                </button>
                            </div>
                        </div>

                        <div className="glass dark:bg-gray-800/50 shadow-2xl rounded-3xl p-6 md:p-10 border border-white/20 dark:border-gray-700/50 mb-12">
                            <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-4">{t('profilePage_help_support_title')}</h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-2xl">{t('profilePage_restart_onboarding_desc')}</p>
                            <button
                                onClick={onRestartOnboarding}
                                className="bg-indigo-600/10 hover:bg-indigo-600/20 text-indigo-600 dark:text-indigo-400 font-bold py-3.5 px-8 rounded-xl transition-all border border-indigo-200/50 dark:border-indigo-500/20 active:scale-95"
                            >
                                {t('profilePage_restart_onboarding_button')}
                            </button>
                        </div>
                    </>
                )}

            </div>
            <Footer onNavigate={onNavigate} />

            {/* Color Analysis Modal for Auto-Detect */}
            <ColorAnalysisModal
                isOpen={isAnalysisModalOpen}
                onClose={() => setIsAnalysisModalOpen(false)}
                result={null}
                onSaveToProfile={handleUpdateProfileFromAnalysis}
            />

            {/* Payment Modal */}
            {pendingUpgrade && (
                <PaymentModal
                    isOpen={isPaymentModalOpen}
                    onClose={() => { setIsPaymentModalOpen(false); setPendingUpgrade(null); }}
                    tierId={pendingUpgrade.tierId}
                    tierName={pendingUpgrade.tierName}
                    price={formatPrice(Number(pendingUpgrade.price))}
                    originalPriceEUR={pendingUpgrade.originalPriceEUR}
                    onSuccess={handlePaymentSuccess}
                />
            )}
            {/* Admin Management Modal */}
            {
                isAdmin && (
                    <AdminManagementModal
                        isOpen={isAdminModalOpen}
                        onClose={() => setIsAdminModalOpen(false)}
                        allUserProfiles={allUserProfiles}
                    />
                )
            }
        </motion.div >
    );
};

export default ProfilePage;
